import React, { useState } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Button, Dialog, DialogContent, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Download, Close, ZoomIn } from '@mui/icons-material';
import PageWrapper from '../components/PageWrapper';
import SectionDivider from '../components/SectionDivider';

const scheduleData = [
    { time: '9:00 – 9:15', activity: 'Homeroom – Morning Meeting', detail: 'Sharing time, new topics, challenges, building community connections' },
    { time: '9:15 – 10:40', activity: 'Language Arts / English, & Independent Work', detail: 'In-person virtual meetings · No recordings' },
    { time: '10:40 – 11:00', activity: 'Break', detail: '20 minutes' },
    { time: '11:00 – 12:30', activity: 'Mathematics', detail: null },
    { time: '12:30 – 1:00', activity: 'Lunch', detail: 'Open mic days and silent independent days during lunch' },
    { time: '1:00 – 2:00', activity: 'Science / Social Studies', detail: 'Science (Mon & Thu) · Social Studies/Humanities (Tue & Fri)' },
    { time: '2:00 – 2:45', activity: 'Special Area Subjects', detail: 'World Languages, Art & Music' },
];

const FLYER_IMAGE_URL = '/assets/images/MacGillis_Interactive_Wednesdays_Flyer_MD_Certified.png';
const FLYER_FILENAME = 'MacGillis_Interactive_Wednesdays_Flyer_MD_Certified.png';

const Academics = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [flyerModalOpen, setFlyerModalOpen] = useState(false);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = `${process.env.PUBLIC_URL}${FLYER_IMAGE_URL}`;
        link.download = FLYER_FILENAME;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <PageWrapper configKey="academics" pageTitle="Academics and Curriculum">
            {(config) => (
                <Container maxWidth="md" sx={{ mt: 3, mb: 3 }}>
                    <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                        For the Love of Wisdom
                    </Typography>
                    <Typography component="p" sx={{ mb: 1 }}>
                        MGA's main priority is to inspire young people to explore their curiosity to its farthest reaches and beyond. Our motto, "for the love of wisdom" (ad sapietiam diligendum) reflects our belief that young people should have opportunities to engage their minds as broadly and deeply in their interests without limit.  MGA is dedicated to providing education that ignites our students' passion for exploration and discovery that goes far beyond grades and GPA attainment. We are committed to providing a fusion of innovative and traditional curriculum and instruction that engages, challenges, enlightens and empowers.
                        <br />
                        <br />
                        In support of our aims, the Lower and Upper School curriculums are grounded in the following learning standards: in ELA - National Common Core Standards (CCSS); in Social Studies - National Council of Social Studies (NCSS); in Science - Next Generation of Science Standards (NGSS); and in Mathematics - National Council Teachers of Math (NCTM). 
                    </Typography>

                    <SectionDivider sx={{ my: 6 }} />

                    <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                        Lower School Curriculum
                    </Typography>
                    <Typography component="p" sx={{ mb: 1 }}>
                        Lower School classes are aimed at establishing a lifelong love of learning as early as possible. We encourage our students to ask questions and tap into their natural curiosity through engaging curriculum, instruction, and creation. We value individualized attention and collaboration to address our students' learning needs, and our students quickly realize that positive learning experiences are built on caring and respectful relationships between their teachers and each other.  As a result, our students gain both a strong academic foundation and limitless intellectual growth.
                    </Typography>

                    <Typography variant="h3" component="h2" gutterBottom sx={{ mt: 6, textAlign: { xs: 'left', sm: 'center' } }}>
                        Upper School Curriculum
                    </Typography>
                    <Typography component="p" sx={{ mb: 1 }}>
                        Upper School classes are aimed at nurturing a lifelong love of learning and enabling students to try new things, take more risks, navigate failures to build their resilience, and develop intellectual independence.  Similar to our Lower School, Upper School classes are small to allow teachers and students to develop positive and caring relationships.  Students in the Upper school also benefit from individualized attention, collaborative tasks, and engaging curriculum directed towards empowering students to learn to the extent of their imagination.  As a result, our students develop the skills to approach complex and rigorous academic challenges with confidence in themselves and respect for those around them.
                    </Typography>

                    <SectionDivider sx={{ my: 6 }} />

                    <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                        Typical Daily Schedule
                    </Typography>
                    <Typography component="p" sx={{ mb: 3, textAlign: 'center' }}>
                        Fulltime Student — Monday, Tuesday, Thursday &amp; Friday
                    </Typography>

                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', width: { xs: '35%', sm: '25%' }, fontSize: '0.95rem' }}>
                                        Time
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                        Activity
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduleData.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? 'grey.50' : 'white',
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 600, color: 'primary.dark', verticalAlign: 'top', py: 1.5 }}>
                                            {row.time}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Typography sx={{ fontWeight: 600 }}>
                                                {row.activity}
                                            </Typography>
                                            {row.detail && (
                                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                                    {row.detail}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{
                        backgroundColor: 'secondary.main',
                        borderRadius: 2,
                        px: 3,
                        py: 2,
                    }}>
                        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                            Wednesdays are Interactive!
                        </Typography>
                        <Typography variant="body2">
                            Field Trips, PE Activities, Art Appreciation, Dance, Kids Zones, Nature Hikes, Virtual or In-person Museum Tours, Special Science/Humanities Lab Days, &amp; more.
                        </Typography>
                    </Box>

                    <SectionDivider sx={{ my: 6 }} />

                    <Box sx={{ backgroundColor: '#f5f5f5', py: 4, px: { xs: 2, sm: 4 }, borderRadius: 2 }}>
                        <Typography
                            variant="h3"
                            component="h2"
                            gutterBottom
                            sx={{ mb: 4, textAlign: 'center' }}
                        >
                            Interactive Wednesdays
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    maxWidth: '600px',
                                    width: '100%',
                                    backgroundColor: '#fff',
                                    padding: { xs: 2, sm: 3 },
                                    borderRadius: 1,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                                    border: '1px solid #e0e0e0',
                                    cursor: isMobile ? 'default' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: isMobile ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)' : '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                                        transform: isMobile ? 'none' : 'translateY(-2px)',
                                    }
                                }}
                                onClick={isMobile ? undefined : () => setFlyerModalOpen(true)}
                            >
                                {!isMobile && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(0, 0, 0, 0)',
                                            transition: 'background-color 0.3s ease',
                                            borderRadius: 1,
                                            zIndex: 1,
                                            pointerEvents: 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                            }
                                        }}
                                    >
                                        <ZoomIn
                                            sx={{
                                                fontSize: 64,
                                                color: 'white',
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                                                '.MuiBox-root:hover &': {
                                                    opacity: 1
                                                }
                                            }}
                                        />
                                    </Box>
                                )}

                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}${FLYER_IMAGE_URL}`}
                                    alt="Interactive Wednesdays Flyer"
                                    sx={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                        borderRadius: 1,
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {!isMobile && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="large"
                                        startIcon={<ZoomIn />}
                                        onClick={() => setFlyerModalOpen(true)}
                                        sx={{ px: 3, py: 1.5, borderRadius: 2 }}
                                    >
                                        View Full Size
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    startIcon={<Download />}
                                    onClick={handleDownload}
                                    sx={{ px: 3, py: 1.5, borderRadius: 2 }}
                                >
                                    Download Flyer
                                </Button>
                            </Box>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textAlign: 'center', mt: 1 }}
                            >
                                {isMobile
                                    ? 'Download to print and share!'
                                    : 'Click the image or "View Full Size" to enlarge. Download to print and share!'
                                }
                            </Typography>
                        </Box>

                        <Dialog
                            open={flyerModalOpen}
                            onClose={() => setFlyerModalOpen(false)}
                            maxWidth={false}
                            PaperProps={{
                                sx: {
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    maxWidth: '95vw',
                                    maxHeight: '95vh',
                                    overflow: 'hidden'
                                }
                            }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    onClick={() => setFlyerModalOpen(false)}
                                    sx={{
                                        position: 'absolute',
                                        top: -48,
                                        right: 0,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)'
                                        },
                                        zIndex: 1
                                    }}
                                >
                                    <Close />
                                </IconButton>

                                <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}${FLYER_IMAGE_URL}`}
                                        alt="Interactive Wednesdays Flyer - Full Size"
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: '95vh',
                                            width: 'auto',
                                            height: 'auto',
                                            display: 'block',
                                            borderRadius: 1,
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                        }}
                                    />
                                </DialogContent>
                            </Box>
                        </Dialog>
                    </Box>
                </Container>
            )}
        </PageWrapper>
    );
};

export default Academics;